import React, { useState, useEffect } from 'react';
import { ref, onValue } from 'firebase/database';
import { db } from '../../../integrations/firebase';
import { NotificationService } from '../services/NotificationService';
import NotificationToast from './NotificationToast';

// Sound file path (assuming it exists or will be added)
const NOTIFICATION_SOUND = '/sounds/notification.mp3';

const NotificationManager = ({ userId, children }) => {
    const [notifications, setNotifications] = useState([]);
    const [activeToast, setActiveToast] = useState(null);
    const audioRef = React.useRef(null);

    useEffect(() => {
        audioRef.current = new Audio(NOTIFICATION_SOUND);
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!userId) return;

        const notificationsRef = ref(db, `notifications/${userId}`);

        // 1. Listen for full list updates (for Bell/Dropdown)
        const unsubscribeList = onValue(notificationsRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const list = Object.entries(data).map(([key, val]) => ({
                    id: key,
                    ...val
                })).sort((a, b) => b.timestamp - a.timestamp);
                setNotifications(list);
            } else {
                setNotifications([]);
            }
        });

        // 2. Listen for NEW notifications (for Toast & Sound)
        // We use a separate listener logic or just diffing. 
        // Using the Service's onChildAdded wrapper is cleaner for "events".
        const unsubscribeToast = NotificationService.listenForNewNotifications(userId, (notification) => {
            setActiveToast(notification);
            // Play sound
            audioRef.current.play().catch(e => console.log("Audio play failed:", e));
        });

        // 3. Request FCM Permission & Listen for Foreground Messages
        // Guard against repeated requests
        if (!window.hasRequestedFcmPermission) {
            window.hasRequestedFcmPermission = true;
            NotificationService.requestPermission(userId);
        }

        const unsubscribeFCM = NotificationService.listenForForegroundNotifications((payload) => {
            // Convert FCM payload to internal notification format for Toast
            const notification = {
                title: payload.notification?.title || 'New Message',
                message: payload.notification?.body || '',
                type: 'push_notification',
                link: payload.data?.link || '/dashboard',
                timestamp: Date.now()
            };
            setActiveToast(notification);
            audioRef.current.play().catch(e => console.log("Audio play failed:", e));
        });

        return () => {
            unsubscribeList();
            unsubscribeToast();
            if (unsubscribeFCM) unsubscribeFCM();
        };
    }, [userId]);

    return (
        <>
            {children(notifications)}
            {activeToast && (
                <NotificationToast
                    notification={activeToast}
                    onClose={() => setActiveToast(null)}
                />
            )}
        </>
    );
};

export default NotificationManager;
